import {
    createLogPrefix,
    globalRef,
    logError,
    logWarn,
    type Unsubscribe,
    withTimeout,
} from '@amazon/vinyl-util'
import { flushMetrixUploaders, type SessionEndHook } from '@amzn/bf-playback'
import { yieldTask } from '@/util/timeSlice'

export const SESSION_END_HOOKS_TIMEOUT_SECONDS = 2

const target = { logPrefix: createLogPrefix('sessionEndHooks') }

const hooksRef = globalRef(() => new Set<SessionEndHook>())

export function addSessionEndHook(hook: SessionEndHook): Unsubscribe {
    const hooks = hooksRef.value
    hooks.add(hook)
    return () => hooks.delete(hook)
}

/**
 * Runs every hook while the session still authenticates and waits until they settle or the
 * timeout passes, so a hung report can't trap the customer. Never rejects: a failed hook is logged
 * and must not block the session's end.
 */
export async function runSessionEndHooks(): Promise<void> {
    const settled = Promise.all(
        [...hooksRef.value].map(async (hook) => {
            try {
                await hook()
            } catch (e) {
                logError(target, e)
            }
        })
    )
    try {
        await withTimeout(settled, SESSION_END_HOOKS_TIMEOUT_SECONDS)
    } catch (e) {
        logWarn(target, 'session-end hooks timed out', e)
    }
}

/**
 * Uploads the session's unsent metrics while it still authenticates, the ones its end hooks just
 * emitted included, as GH's sign-out flush does. Bounded like the hooks; never rejects.
 */
export async function flushSessionMetrics(): Promise<void> {
    try {
        // One task lets metrics the hooks emitted asynchronously reach the uploaders first.
        await withTimeout(yieldTask().then(flushMetrixUploaders), SESSION_END_HOOKS_TIMEOUT_SECONDS)
    } catch (e) {
        logWarn(target, 'session metrics flush failed or timed out', e)
    }
}
